/**
 * LA CONSOLE SAIT QU'ELLE EST PÉRIMÉE — AB#888, 17 octobre 2026.
 *
 * Le fondateur a déroulé 74 chapitres sans pagination sur une console qui la
 * servait depuis la veille au soir : son onglet tournait encore le code
 * d'avant. La console est une application d'une seule page, servie en UN
 * fichier (`/assets/index-<empreinte>.js`) : passer d'un écran à l'autre ne
 * redemande jamais `index.html`, et un onglet garde le code de son ouverture
 * aussi longtemps qu'il reste ouvert.
 *
 * Ici, on relit `index.html` (sans cache) de temps en temps, et quand il
 * nomme un autre fichier que celui qui tourne, la console est PÉRIMÉE : à la
 * navigation suivante, on recharge l'adresse demandée au lieu de la rendre
 * avec l'ancien code. Rien n'est perdu : on quittait l'écran de toute façon.
 *
 * Sous Vite (développement), aucun fichier `/assets/index-…` : rien n'est
 * vérifié.
 */

const MOTIF = /\/assets\/index-[\w-]+\.js/;

/** Le fichier JavaScript nommé par un `index.html` ; `null` s'il n'en nomme aucun. */
export function scriptDeLIndex(html: string): string | null {
  return MOTIF.exec(html)?.[0] ?? null;
}

/** Le fichier qui tourne dans cet onglet ; `null` hors d'un build (Vite). */
export function scriptCourant(doc: Pick<Document, 'querySelectorAll'> = document): string | null {
  for (const s of Array.from(doc.querySelectorAll<HTMLScriptElement>('script[src]'))) {
    const nom = scriptDeLIndex(s.getAttribute('src') ?? '');
    if (nom !== null) return nom;
  }
  return null;
}

/**
 * Périmé ? Seulement quand les DEUX noms sont connus et diffèrent : une lecture
 * qui échoue, ou un `index.html` illisible, ne recharge jamais rien.
 */
export function estPerime(courant: string | null, servi: string | null): boolean {
  return courant !== null && servi !== null && courant !== servi;
}

/** Relit `index.html` sans cache ; `null` sur toute erreur. */
export async function scriptServi(): Promise<string | null> {
  try {
    const r = await fetch('/', { cache: 'no-store', headers: { accept: 'text/html' } });
    if (!r.ok) return null;
    return scriptDeLIndex(await r.text());
  } catch {
    return null;
  }
}

/** Toutes les cinq minutes, et à chaque retour sur l'onglet. */
export const INTERVALLE_VERIFICATION_MS = 5 * 60 * 1000;
